import { Checkbox, IconButton, InputAdornment } from '@mui/material';
import { ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ReactComponent as EyeIcon } from 'assets/icons/eye.svg';
import { ReactComponent as EyeSlashIcon } from 'assets/icons/eyeSlash.svg';
// import { ReactComponent as ChatIcon } from 'assets/icons/messageText.svg';
import { Form, FormGroup, FullPageProgress, TextFieldController } from 'components';
import { FIELDS_VALUE_LENGTH } from 'constants/fieldsLength';
import { useWindowSize } from 'hooks';
import { Links } from 'settings';
import { required } from 'validations';

import { GetStartedButton } from '../../components';
import { useSignInForm } from './hooks';
import { emailFieldRules } from './rules';
import {
  LinkW,
  LoginButton,
  RecoverPasswordHint,
  RememberMeLabel,
  SignUpHint,
  // SupportIcon,
  // SupportLink,
  // SupportRow,
} from './styled';
import { pillFieldSx } from './styles';

interface SignInFormProps {
  firstTimeSignIn: boolean;
}

export function SignInForm({ firstTimeSignIn }: SignInFormProps): ReactElement {
  const { control, handleFormSubmit, isLoading } = useSignInForm();
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      {isLoading && <FullPageProgress />}

      <Form sx={{ marginTop: '24px' }} onSubmit={handleFormSubmit}>
        <FormGroup marginBottom={isMobile ? 16 : 20}>
          <TextFieldController
            name="email"
            id="email"
            control={control}
            type="email"
            label={t('login.form.email')}
            placeholder={t('login.form.email')}
            rules={emailFieldRules}
            maxLength={FIELDS_VALUE_LENGTH.email.max}
            sx={pillFieldSx}
          />
        </FormGroup>
        <FormGroup marginBottom={isMobile ? 16 : 20}>
          <TextFieldController
            name="password"
            control={control}
            label={t('login.form.password')}
            placeholder={t('login.form.password')}
            type={showPassword ? 'text' : 'password'}
            rules={required}
            maxLength={FIELDS_VALUE_LENGTH.password.max}
            sx={pillFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('login.form.togglePasswordVisibility')}
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FormGroup>
        <FormGroup
          marginBottom={24}
          sx={{
            justifyContent: 'space-between',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <RememberMeLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(_, checked) => setRememberMe(checked)}
                size="small"
                color="info"
              />
            }
            label={t('login.form.rememberMe')}
          />
          <RecoverPasswordHint>
            <LinkW to={Links.auth.requestPasswordReset}>{t('login.recovery')}</LinkW>
          </RecoverPasswordHint>
        </FormGroup>

        {firstTimeSignIn ? (
          <FormGroup sx={{ justifyContent: 'flex-end' }}>
            <GetStartedButton />
          </FormGroup>
        ) : (
          <LoginButton type="submit" variant="contained" color="info">
            {t('login.signInBtn')}
          </LoginButton>
        )}
      </Form>

      {!firstTimeSignIn && (
        <SignUpHint sx={{ marginTop: '20px' }}>
          <Trans i18nKey="login.noAccount" values={{ linkLabel: t('signUp.signUpBtn') }}>
            Don&apos;t have an account?
            <LinkW to={Links.auth.registration.index}>{t('signUp.signUpBtn')}</LinkW>
          </Trans>
        </SignUpHint>
      )}

      {/* <SupportRow sx={{ marginTop: isMobile ? '32px' : '56px' }}>
        <SupportIcon>
          <ChatIcon />
        </SupportIcon>
        <SupportLink>{t('login.customerSupport')}</SupportLink>
      </SupportRow> */}
    </>
  );
}
